import type { Query, QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';

import type { PositionArchitecture, PositionBootstrap, PositionDetail } from '../../data-contract/position-management';
import type {
    PositionArchitectureTarget,
    PositionTarget,
} from '../../fetchers/position-management/position-management-fetchers';
import { isPositionAccessLossError, isStaleArchitectureError } from '../position-error';
import { positionManagementKeys, TRANSFORMATION_SEGMENT, WORK_QUEUE_COUNTS_SEGMENT } from './query-keys';

const isWorkspaceQuery = (query: Query): boolean => query.queryKey.at(-1) === 'workspace';

const ARCHITECTURE_AREA_KEY_INDEX = 3;

const isArchitecturePositionsQuery = (query: Query): boolean =>
    query.queryKey[ARCHITECTURE_AREA_KEY_INDEX] === 'positions';

const isPositionDetailQuery = (query: Query): boolean => query.queryKey.at(-2) === 'detail';

/**
 * Marks reads another page derives from positions, such as the AI transformation strategy: every change that refreshes
 * the position lists, made anywhere including the chat, reads them again too.
 */
export const POSITION_DERIVED_QUERY_META = { derivedFromPositions: true } as const;

const isPositionDerivedQuery = (query: Query): boolean => query.meta?.['derivedFromPositions'] === true;

/** Reads every cached position detail again, for a change that touches many positions at once, such as their lead. */
export const invalidatePositionDetails = (queryClient: QueryClient): Promise<void> =>
    queryClient.invalidateQueries({ predicate: isPositionDetailQuery, queryKey: positionManagementKeys.anyPosition() });

/** Cancels derived reads still loading first, as TanStack Query would keep a first load's answer from before. */
export const invalidatePositionDerivedQueries = async (queryClient: QueryClient): Promise<void> => {
    await queryClient.cancelQueries({ predicate: isPositionDerivedQuery });
    await queryClient.invalidateQueries({ predicate: isPositionDerivedQuery });
};

const isWorkQueueCountsQuery = (query: Query): boolean => query.queryKey.at(-1) === WORK_QUEUE_COUNTS_SEGMENT;

const isUnalignedListQuery = (query: Query): boolean => query.queryKey.includes('unaligned');

/** An alignment change can move its position in or out of the Unaligned queue, so it re-reads the counts and that list. */
export const invalidateUnalignedQueue = (queryClient: QueryClient): Promise<void> =>
    queryClient.invalidateQueries({
        predicate: (query) => isWorkQueueCountsQuery(query) || isUnalignedListQuery(query),
        queryKey: positionManagementKeys.architectures(),
    });

const isTransformationListQuery = (query: Query): boolean => query.queryKey.at(-1) === TRANSFORMATION_SEGMENT;

/** Refreshes the position lists that show AI transformation facts, for status or priority changes made elsewhere. */
export const invalidatePositionTransformationLists = (queryClient: QueryClient): Promise<void> =>
    queryClient.invalidateQueries({
        predicate: isTransformationListQuery,
        queryKey: positionManagementKeys.architectures(),
    });

export const invalidatePositionListCaches = (queryClient: QueryClient): void => {
    void queryClient.invalidateQueries({
        predicate: (query) => !isWorkspaceQuery(query),
        queryKey: positionManagementKeys.architectures(),
    });
    void queryClient.invalidateQueries({ queryKey: positionManagementKeys.levelPositionCounts() });
    void invalidatePositionDerivedQueries(queryClient);
};

const POSITION_ID_KEY_INDEX = 2;

export const handleAccessLoss = (queryClient: QueryClient, error: unknown, target?: PositionTarget): boolean => {
    if (!isPositionAccessLossError(error)) return false;

    void queryClient.invalidateQueries({ queryKey: positionManagementKeys.bootstrap(), refetchType: 'all' });
    invalidatePositionListCaches(queryClient);
    void queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[POSITION_ID_KEY_INDEX] !== target?.positionId,
        queryKey: positionManagementKeys.anyPosition(),
    });
    if (target !== undefined) {
        void queryClient.resetQueries({ queryKey: positionManagementKeys.position(target.positionId) });
    }

    return true;
};

export const invalidateArchitecturePositionCaches = (
    queryClient: QueryClient,
    { architectureId }: PositionArchitectureTarget,
): void => {
    void queryClient.invalidateQueries({ queryKey: positionManagementKeys.positions(architectureId) });
    void invalidatePositionDerivedQueries(queryClient);
};

/** A generated plan fills the whole architecture, so everything read for it is stale. */
export const invalidatePlannedArchitectureCaches = (
    queryClient: QueryClient,
    { architectureId }: PositionArchitectureTarget,
): void => {
    void queryClient.invalidateQueries({ queryKey: positionManagementKeys.bootstrap() });
    void queryClient.invalidateQueries({ queryKey: positionManagementKeys.architecture(architectureId) });
    void queryClient.invalidateQueries({ queryKey: positionManagementKeys.levelPositionCounts() });
    void invalidatePositionDerivedQueries(queryClient);
};

export const invalidatePositionCaches = (queryClient: QueryClient, { positionId }: PositionTarget): Promise<void> => {
    invalidatePositionListCaches(queryClient);
    void queryClient.invalidateQueries({ queryKey: positionManagementKeys.events(positionId) });

    return queryClient.invalidateQueries({ queryKey: positionManagementKeys.positionDetails(positionId) });
};

/** Refreshes a position's list, detail and event reads, for components that change it outside the position mutations. */
export const useInvalidatePositionCaches = (): ((target: PositionTarget) => Promise<void>) => {
    const queryClient = useQueryClient();
    return (target) => invalidatePositionCaches(queryClient, target);
};

export const cacheSavedPosition = (
    queryClient: QueryClient,
    { positionId }: PositionTarget,
    saved: PositionDetail,
): void => {
    invalidatePositionListCaches(queryClient);
    void queryClient.invalidateQueries({ queryKey: positionManagementKeys.position(positionId) });
    queryClient.setQueryData(positionManagementKeys.positionDetail(positionId, saved.version), saved);
};

export const forgetDeletedPosition = (queryClient: QueryClient, { positionId }: PositionTarget): void => {
    queryClient.removeQueries({ queryKey: positionManagementKeys.position(positionId) });
    invalidatePositionListCaches(queryClient);
};

export const invalidateCatalogCaches = async (queryClient: QueryClient): Promise<void> => {
    await queryClient.invalidateQueries({
        predicate: isWorkspaceQuery,
        queryKey: positionManagementKeys.architectures(),
    });
    invalidatePositionListCaches(queryClient);
    void queryClient.invalidateQueries({ queryKey: positionManagementKeys.anyPosition() });
};

export const invalidateWorkspaceCaches = (
    queryClient: QueryClient,
    { architectureId }: PositionArchitectureTarget,
): Promise<void> => queryClient.invalidateQueries({ queryKey: positionManagementKeys.workspace(architectureId) });

export const invalidateAttributeCaches = async (queryClient: QueryClient): Promise<void> => {
    await queryClient.invalidateQueries({
        predicate: isWorkspaceQuery,
        queryKey: positionManagementKeys.architectures(),
    });
    void queryClient.invalidateQueries({
        predicate: isArchitecturePositionsQuery,
        queryKey: positionManagementKeys.architectures(),
    });
    void invalidatePositionDerivedQueries(queryClient);
};

const cachedPositionIds = (
    queryClient: QueryClient,
    { architectureId }: PositionArchitectureTarget,
): ReadonlySet<string> =>
    new Set(
        queryClient
            .getQueriesData<PositionDetail>({
                predicate: isPositionDetailQuery,
                queryKey: positionManagementKeys.anyPosition(),
            })
            .flatMap(([, detail]) => (detail?.architecture_id === architectureId ? [detail.id] : [])),
    );

export const forgetDeletedArchitecture = (queryClient: QueryClient, target: PositionArchitectureTarget): void => {
    for (const positionId of cachedPositionIds(queryClient, target)) {
        queryClient.removeQueries({ queryKey: positionManagementKeys.position(positionId) });
    }
    queryClient.removeQueries({ queryKey: positionManagementKeys.architecture(target.architectureId) });
};

type BootstrapUpdate = (bootstrap: PositionBootstrap | undefined) => PositionBootstrap | undefined;

export const cacheUpdatedArchitecture =
    (update: (architecture: PositionArchitecture) => BootstrapUpdate) =>
    async <Target>(queryClient: QueryClient, _target: Target, architecture: PositionArchitecture): Promise<void> => {
        await queryClient.cancelQueries({ queryKey: positionManagementKeys.bootstrap() });
        queryClient.setQueryData(positionManagementKeys.bootstrap(), update(architecture));
        // Its connected Job Architecture or managers' edit rights change what derived pages show and allow.
        void invalidatePositionDerivedQueries(queryClient);
    };

export const refreshStaleArchitectures = (queryClient: QueryClient, error: unknown): void => {
    if (isStaleArchitectureError(error)) {
        void queryClient.invalidateQueries({ queryKey: positionManagementKeys.bootstrap() });
    }
};
